import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { ArrowDownIcon } from "@heroicons/react/24/solid";
import {
  Card,
  Select,
  Option,
  Dialog,
  Chip,
  CardHeader,
  Input,
  Typography,
  Button,
  CardBody,
  CardFooter,
  IconButton,
  Tooltip,
} from "@material-tailwind/react";
import { useLocation } from "react-router-dom";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import TableSkeleton from "../../components/Loaders/TableSkeleton";
import {
  getAllVendorsResponse,
  updateQuestionResponseDataBasedOnReviewer,
} from "../../helperFunctions/apiFunction";
import TableRowWithShowMore from "../../components/TableRowWithShowMore";
import LoadingButton from "../../components/Loaders/LoadingButton";
import toast from "react-hot-toast";
import SwitchResponseReview from "./SwitchResponseReview";

const ResponseReview = () => {
  const { completeAuthData: authData, refreshToken } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(null);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [searchBy, setSearchBy] = useState("");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const location = useLocation();
  const { vendorId, questionnaireId, vendorQuestionnaireId } =
    location.state || {};
  const [reloadData, setReloadData] = useState(false);
  const [switchStates, setSwitchStates] = useState([]);
  const tableHead = ["Question List", "Response", "Status", "Action"];

  useEffect(() => {
    setIsLoading(null);
    getAllVendorsResponse(
      refreshToken,
      authData?.id,
      vendorId,
      questionnaireId,
      limit,
      page,
      status,
      vendorQuestionnaireId
    ).then((data) => {
      if (data?.code === 440)
        toast.error(data?.message, {
          className: "text-[12px]",
        });
      setTotalPage(data?.data?.totalPages);
      setTemplateData(data?.data?.results);
      setIsLoading(false);
    });
  }, [reloadData]);

  useEffect(() => {
    if (templateData) {
      const initialStates = templateData
        .filter((item) => item?.response?.status === "submitted")
        .map((item) => ({
          _id: item?.id,
          status: "accepted",
        }));
      setSwitchStates(initialStates);
    }
  }, [templateData]);

  const exportToExcel = () => {};
  const handleClick = () => {
    setOpen(true);
  };

  const handleChange = (checked, id) => {
    setSwitchStates((prevState) =>
      prevState.map((state) =>
        state._id === id
          ? { ...state, status: checked ? "accepted" : "rejected" }
          : state
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoadingButton(true);
    updateQuestionResponseDataBasedOnReviewer(refreshToken, [...switchStates])
      .then((response) => {
        if (response?.code === 200 || response?.code === 201) {
          toast.success(`Response submitted successfully`, {
            className: "text-[12px]",
          });
        } else if (response.code === 400) {
          toast.error(response?.message, {
            className: "text-[12px]",
          });
        } else {
          toast.error(response?.message, {
            className: "text-[12px]",
          });
        }
      })
      .catch((error) => {
        toast.error(error.message, {
          className: "text-[12px]",
        });
        console.error("Error during login:", error.message);
      })
      .finally(() => {
        setIsLoadingButton(false);
        setOpen((cur) => !cur);
        setSwitchStates([]);
        setReloadData((cur) => !cur);
      });
  };

  return (
    <div className="app-main__inner !pl-20">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Questionnaire
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all questionnaire and their responses
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              <Button
                variant="outlined"
                size="sm"
                onClick={exportToExcel}
                loading={isLoading === null ? true : false}
              >
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
              <Select
                color="blue"
                label="Select Limit"
                className="!border-t-0 !border-[#b0bec5]"
                onChange={(event) => {
                  setLimit(event);
                  setIsLoading(null);
                }}
                value={limit}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={50}>50</Option>
              </Select>
              <Select
                color="green"
                label="Status"
                value={status}
                className="!border-t-0 !border-[#b0bec5]"
                onChange={(event) => {
                  setStatus(event);
                  setIsLoading(null);
                }}
              >
                <Option value="">All Data</Option>
                <Option value="submitted">Submitted</Option>
                <Option value="assigned">Assigned</Option>
                <Option value="rejected">Rejected</Option>
              </Select>
              <>
                <Tooltip
                  className="bg-blue-gray-500"
                  content="Submit all Responses"
                >
                  <button
                    className={`align-middle select-none font-sans font-bold text-center uppercase transition-all 
                    text-xs py-2 px-4 rounded-lg bg-gradient-to-tr from-green-600 to-green-400 text-white shadow-md shadow-green-500/20 hover:shadow-lg hover:shadow-green-500/40
                    active:opacity-[0.85] ${
                      switchStates.length === 0
                        ? "cursor-not-allowed opacity-60"
                        : false
                    }`}
                    type="button"
                    onClick={handleClick}
                    disabled={switchStates.length === 0 ? true : false}
                  >
                    Submit&nbsp;Responses
                  </button>
                </Tooltip>
              </>
            </div>
            <div className="w-full md:w-auto">
              <Input
                label="Search"
                value={searchBy}
                className="focus:!border-black focus:!border-2 focus:!border-t-0"
                onChange={(e) => setSearchBy(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="px-3 p-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {tableHead.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50 w-32"
                  >
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                    >
                      {head}{" "}
                      {index !== tableHead.length - 1 && (
                        <ChevronUpDownIcon
                          strokeWidth={2}
                          className="h-4 w-4"
                        />
                      )}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            {isLoading === null ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={tableHead.length}>
                      <TableSkeleton />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : templateData && templateData.length > 0 ? (
              <tbody>
                {templateData.map((item, index) => {
                  const isLast = index === templateData.length - 1;
                  const classes = isLast
                    ? "p-4"
                    : "p-4 border-b border-blue-gray-50";
                  return (
                    <tr key={`${item?.id}-${index}`}>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                        >
                          {item?.quesListName &&
                          item?.quesListName.length > 40 ? (
                            <TableRowWithShowMore data={item?.quesListName} />
                          ) : (
                            item?.quesListName
                          )}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                        >
                          {item?.response?.ans &&
                          item?.response?.ans.length > 40 ? (
                            <TableRowWithShowMore data={item?.response?.ans} />
                          ) : (
                            item?.response?.ans
                          )}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <div className="w-max">
                          <Chip
                            variant="ghost"
                            size="sm"
                            value={item?.response?.status}
                            color={
                              item?.response?.status === "submitted"
                                ? "blue-gray"
                                : item?.response?.status === "rejected"
                                ? "red"
                                : item?.response?.status === "accepted"
                                ? "green"
                                : "black"
                            }
                          />
                        </div>
                      </td>
                      <td className={classes}>
                        {item?.response?.status === "submitted" ? (
                          <>
                            <div>
                              <SwitchResponseReview
                                id={item?.id}
                                isChecked={
                                  switchStates.find(
                                    (state) => state._id === item?.id
                                  )?.status === "accepted"
                                }
                                handleChange={handleChange}
                              />
                            </div>
                          </>
                        ) : (
                          <></>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ) : (
              <>
                <tr>
                  <td colSpan="4">
                    <div className="flex flex-col items-center w-full">
                      <img
                        height={250}
                        width={250}
                        quality={100}
                        src="/assets/images/9276430.jpg"
                        className="w-1/4 mix-blend-darken"
                        alt=""
                      />
                      <p className="text-[18px] font-extralight">
                        No Data Found!
                      </p>
                    </div>
                  </td>
                </tr>
              </>
            )}
          </table>
        </CardBody>
        {isLoading === null ? null : (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page - 1);
                  setIsLoading(null);
                }}
                disabled={totalPage < 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setPage(index + 1);
                      setIsLoading(null);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                ))
                  .reduce((acc, button, index, array) => {
                    if (
                      index < 3 ||
                      index >= totalPage - 3 ||
                      (index >= Math.max(page - 1, 3) &&
                        index <= Math.min(page + 1, totalPage - 4))
                    ) {
                      acc.push(button);
                    } else if (acc[acc.length - 1] !== "ellipsis") {
                      acc.push("ellipsis");
                    }
                    return acc;
                  }, [])
                  .map((element, index, array) => {
                    return element === "ellipsis" ? (
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    ) : index > 0 && array[index - 1] === "ellipsis" ? (
                      <React.Fragment key={`fragment-${index}`}>
                        {element}
                      </React.Fragment>
                    ) : (
                      element
                    );
                  })}
              </div>

              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page + 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        )}
      </Card>

      <Dialog
        size="sm"
        open={open}
        handler={setOpen}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <form onSubmit={handleSubmit}>
          <Card className="mx-auto w-full p-2">
            <CardBody className="flex flex-col gap-4">
              <div className="grid grid-cols-1">
                <div>
                  <Typography className="mb-1" variant="h6">
                    Are you sure you want to submit?
                  </Typography>
                </div>
              </div>
            </CardBody>
            <CardFooter className="flex justify-end py-2">
              <Button
                variant="text"
                color="red"
                onClick={() => {
                  setOpen(false);
                }}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>
              {isLoadingButton ? (
                <LoadingButton isSetText={true} isLoading={isLoading} />
              ) : (
                <Button
                  variant="gradient"
                  type="submit"
                  color="green"
                  disabled={switchStates.length === 0 ? true : false}
                >
                  Submit
                </Button>
              )}
            </CardFooter>
          </Card>
        </form>
      </Dialog>
    </div>
  );
};

export default ResponseReview;
